/**
 * The app-tour catalogue.
 *
 * Editorial rules, because a tour that breaks them is the thing people
 * complain about:
 *
 *  - **Few stops.** Nine is the ceiling, and a user with no plans sees four.
 *    A tour is an orientation, not a manual; the last stop hands people the
 *    row that restarts it rather than trying to cover what it left out.
 *  - **One idea per stop**, two sentences at most.
 *  - **Say what the thing is for, not what it is.** "Plans" on a nav item
 *    already says "Plans". The stop earns its place by saying why you'd click.
 *  - **Never gate anything on finishing.** Esc, the ×, and clicking the dimmed
 *    backdrop all end the tour, and it never starts on its own.
 *
 * Steps whose `appliesTo` is false are dropped when the tour starts, so the
 * "n of m" counter always reflects the stops this person will actually see.
 */

import { navigationRef } from '../navigation/navigationRef';
import { TOUR_ANCHORS } from './anchors';
import type { TourStep } from './types';

/** Navigate to a top-level tab, resetting its stack to the index screen. */
function goToTab(tab: 'Plans' | 'Settings') {
  if (!navigationRef.isReady()) return;
  const screen = tab === 'Plans' ? 'ProjectsList' : 'SettingsHome';
  try {
    (navigationRef as any).navigate('Main', { screen: tab, params: { screen } });
  } catch {
    /* best-effort — the overlay skips the step if the anchor never appears */
  }
}

/** Navigate to a project-scoped screen inside the Plans stack. */
function goToProjectScreen(
  screen: string,
  projectId: string | number | null,
  extraParams?: Record<string, unknown>,
) {
  if (!navigationRef.isReady() || !projectId) return;
  try {
    (navigationRef as any).navigate('Main', {
      screen: 'Plans',
      params: { screen, params: { projectId, ...extraParams } },
    });
  } catch {
    /* best-effort */
  }
}

export const TOUR_STEPS: TourStep[] = [
  {
    key: 'sidebar',
    anchor: TOUR_ANCHORS.sidebar,
    title: 'Everything starts here',
    body: 'This rail is how you move around Eleplan. Hover it and it expands to show labels; it collapses back out of your way when you leave.',
    placement: 'right',
    navigate: () => goToTab('Plans'),
  },
  {
    key: 'ellie',
    anchor: TOUR_ANCHORS.navEllie,
    title: 'Ask Ellie anything',
    body: 'Your assistant knows what your plans know — appointments, medications, documents, history — and can act on them: schedule, draft, remind, summarise.',
    placement: 'right',
    appliesTo: (a) => a.hasAccountChat && !a.isPartner,
  },
  {
    key: 'plans',
    anchor: TOUR_ANCHORS.navPlans,
    title: 'One plan per person',
    body: 'Each person you care for gets their own plan, holding their documents, medications, care team, and history in one place.',
    placement: 'right',
    navigate: () => goToTab('Plans'),
  },
  {
    key: 'new-plan',
    anchor: TOUR_ANCHORS.newPlan,
    title: 'Start a plan three ways',
    body: 'Answer a short intake, describe the person in your own words, or just upload a document and let Eleplan read the details out of it.',
    placement: 'bottom',
    navigate: () => goToTab('Plans'),
    appliesTo: (a) => !a.isPartner,
  },
  {
    key: 'plan-setup',
    anchor: TOUR_ANCHORS.planSetupCard,
    title: 'The checklist knows what to do next',
    body: 'Inside a plan, this card ranks the highest-value things still missing. The more a plan knows, the more specific Ellie can be about it.',
    placement: 'top',
    navigate: (a) => goToProjectScreen('ProjectHome', a.firstProjectId),
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-ellie',
    anchor: TOUR_ANCHORS.navProjectEllie,
    title: 'Ellie, scoped to one plan',
    body: 'Open a plan and the rail switches to that plan. Ellie here answers only from this person’s record, and cites the document it drew from.',
    placement: 'right',
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-docs',
    anchor: TOUR_ANCHORS.navProjectDocs,
    title: 'Documents that read themselves',
    body: 'Files, the encrypted Vault, surveys, and benefits live here. Anything you upload is read, summarised, and mined for appointments and medications you approve.',
    placement: 'right',
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'project-team',
    anchor: TOUR_ANCHORS.navProjectTeam,
    title: 'Bring in the people who help',
    body: 'Invite family, caregivers, or a case manager with per-area permissions — which also decide what their Ellie can see.',
    placement: 'right',
    appliesTo: (a) => a.hasProjects && !a.isPartner,
  },
  {
    key: 'restart',
    anchor: TOUR_ANCHORS.settingsTourRow,
    title: 'That’s the tour',
    body: 'Settings holds your profile, security, notifications, and billing — and this row, so you can run the tour again whenever you want.',
    placement: 'top',
    navigate: () => goToTab('Settings'),
  },
];
